import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Action } from "shared/types/Action"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { FilteredRoomActionHandlers } from "./useRoomActionHandlers"

export interface UseRoomActionsArgs
  extends Partial<FilteredRoomActionHandlers> {
  idPrefix: string
  isReadonly?: boolean
  isEditDisabled?: boolean
  isDuplicateDisabled?: boolean
  isCopyDisabled?: boolean
  isAssignAttachmentsDisabled?: boolean
  isAddClarificationRequestDisabled?: boolean
  isDeleteDisabled?: boolean
}

export function useRoomActions({
  idPrefix,
  isReadonly,
  onEdit,
  isEditDisabled,
  onDuplicate,
  isDuplicateDisabled,
  onCopy,
  isCopyDisabled,
  onAssignAttachments,
  isAssignAttachmentsDisabled,
  onAddClarificationRequest,
  isAddClarificationRequestDisabled,
  onDelete,
  isDeleteDisabled
}: UseRoomActionsArgs) {
  const { t } = useTranslation()

  const editAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__room__actions__edit`,
      "data-testid": "room__actions__edit",
      icon: IconName.EDIT,
      label: t("Room.Actions.Update"),
      disabled: isEditDisabled,
      onClick: onEdit ?? undefined
    }),
    [t, idPrefix, isEditDisabled, onEdit]
  )
  const duplicateAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__room__actions__duplicate`,
      "data-testid": "room__actions__duplicate",
      icon: IconName.DUPLICATE,
      label: t("Room.Actions.Duplicate"),
      disabled: isDuplicateDisabled,
      onClick: onDuplicate ?? undefined
    }),
    [t, idPrefix, isDuplicateDisabled, onDuplicate]
  )

  const copyAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__room__actions__copy`,
      "data-testid": "room__actions__copy",
      icon: IconName.SHARE,
      label: t("Room.Actions.Copy"),
      disabled: isCopyDisabled,
      onClick: onCopy ?? undefined
    }),
    [t, idPrefix, isCopyDisabled, onCopy]
  )

  const assignAttachmentsAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__room__actions__assign-attachments`,
      "data-testid": "room__actions__assign-attachments",
      icon: IconName.PAPERCLIP,
      label: t("Room.Actions.AssignAttachments"),
      disabled: isAssignAttachmentsDisabled,
      onClick: onAssignAttachments ?? undefined
    }),
    [t, idPrefix, isAssignAttachmentsDisabled, onAssignAttachments]
  )

  const addClarificationRequestAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__room__actions__add-clarification-request`,
      "data-testid": "room__actions__add-clarification-request",
      icon: IconName.CLARIFICATION_REQUEST,
      label: t("Room.Actions.AddClarificationRequest"),
      disabled: isAddClarificationRequestDisabled,
      onClick: onAddClarificationRequest ?? undefined
    }),
    [t, idPrefix, isAddClarificationRequestDisabled, onAddClarificationRequest]
  )

  const deleteAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__room__actions__delete`,
      "data-testid": "room__actions__delete",
      icon: IconName.BIN,
      label: t("Room.Actions.Delete"),
      isDanger: true,
      disabled: isDeleteDisabled,
      onClick: onDelete ?? undefined
    }),
    [isDeleteDisabled, onDelete]
  )

  const allActions = useMemo<Action[]>(
    () => [
      editAction,
      duplicateAction,
      copyAction,
      assignAttachmentsAction,
      addClarificationRequestAction,
      deleteAction
    ],
    [
      editAction,
      duplicateAction,
      copyAction,
      assignAttachmentsAction,
      addClarificationRequestAction,
      deleteAction
    ]
  )

  const readonlyActions = useMemo<Action[]>(() => [copyAction], [copyAction])

  const actions = useMemo<Action[]>(
    () =>
      (isReadonly ? readonlyActions : allActions).filter(
        action => !!action.onClick
      ),
    [allActions, readonlyActions, isReadonly]
  )

  return {
    actions,
    editAction,
    duplicateAction,
    copyAction,
    assignAttachmentsAction,
    addClarificationRequestAction,
    deleteAction
  }
}
